import { cn } from "@/lib/cn";
import {
  FloatingFocusManager,
  FloatingPortal,
  autoUpdate,
  flip,
  offset,
  size,
  useClick,
  useDismiss,
  useFloating,
  useInteractions,
  useListNavigation,
  useRole,
  useTransitionStyles,
  useTypeahead,
} from "@floating-ui/react";
import classNames from "classnames";
import { Fragment, useRef, useState } from "react";
import { Icon } from "../Icon";
import "./index.scss";

const CONTROLS_SELECTOR = "controls-jr3m1xhXcYEA06u";
const SELECT_SELECTOR = "controls-select-jr3m1xhXcYEA06u";

export const Select = ({
  options,
  setOption,
  isInvalid,
  placeholder,
  errorMessage,
  optionsClassName = "",
  dividerIndex,
}: {
  options: { title: string; value: string }[];
  setOption: (value: string) => void;
  placeholder?: string;
  errorMessage?: string;
  isInvalid?: boolean;
  optionsClassName?: string;
  dividerIndex?: number;
}) => {
  "use no memo";
  const [isOpen, setIsOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState<number | null>(null);
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);

  const { refs, floatingStyles, context } = useFloating({
    placement: "bottom",
    open: isOpen,
    onOpenChange: setIsOpen,
    strategy: "absolute",
    whileElementsMounted: autoUpdate,
    middleware: [
      offset(5),
      flip({ padding: 10 }),
      size({
        apply({ rects, elements, availableHeight }) {
          Object.assign(elements.floating.style, {
            maxHeight: `${availableHeight}px`,
            minWidth: `${rects.reference.width}px`,
          });
        },
        padding: 10,
      }),
    ],
  });

  const listRef = useRef<Array<HTMLElement | null>>([]);
  const listContentRef = useRef(options.map((option) => option.value) || []);
  const isTypingRef = useRef(false);

  const click = useClick(context, { event: "mousedown" });
  const dismiss = useDismiss(context);
  const role = useRole(context, { role: "listbox" });
  const listNav = useListNavigation(context, {
    listRef,
    activeIndex,
    selectedIndex,
    onNavigate: setActiveIndex,
    loop: true,
  });

  const typeahead = useTypeahead(context, {
    listRef: listContentRef,
    activeIndex,
    selectedIndex,
    onMatch: isOpen ? setActiveIndex : setSelectedIndex,
    onTypingChange(isTyping) {
      isTypingRef.current = isTyping;
    },
  });

  const { isMounted, styles } = useTransitionStyles(context);

  const { getReferenceProps, getFloatingProps, getItemProps } = useInteractions(
    [dismiss, role, listNav, typeahead, click],
  );

  const handleSelect = (index: number) => {
    setOption(options[index].value);
    setSelectedIndex(index);
    setIsOpen(false);
  };

  const selectedItemLabel =
    selectedIndex !== null ? options[selectedIndex].title : undefined;

  return (
    <div
      className={classNames({
        [`${SELECT_SELECTOR}--error`]: isInvalid,
      })}
    >
      <div
        ref={refs.setReference}
        aria-labelledby="select-label"
        aria-autocomplete="none"
        className={classNames(`${SELECT_SELECTOR}__select`, {
          "--open": isOpen,
          "--placeholder": !selectedItemLabel,
        })}
        {...getReferenceProps()}
      >
        <span>{selectedItemLabel || placeholder || ""}</span>
        <Icon icon="ri-arrow-down-s-fill" size={20} color="#12033E" />
      </div>

      {isMounted && (
        <FloatingPortal>
          <FloatingFocusManager context={context} modal={false}>
            <div
              className={cn(
                `${SELECT_SELECTOR}__options tw-z-[99999] tw-overflow-x-hidden`,
                {
                  [optionsClassName]: !!optionsClassName,
                },
              )}
              ref={refs.setFloating}
              style={{
                ...floatingStyles,
                ...styles,
              }}
              {...getFloatingProps()}
            >
              {options.map((option, i) => (
                <Fragment key={option.value}>
                  <div
                    ref={(node) => {
                      listRef.current[i] = node;
                    }}
                    role="option"
                    tabIndex={i === activeIndex ? 0 : -1}
                    aria-selected={i === selectedIndex && i === activeIndex}
                    className={`${SELECT_SELECTOR}__option`}
                    {...getItemProps({
                      onClick() {
                        handleSelect(i);
                      },
                      onKeyDown(event) {
                        if (event.key === "Enter") {
                          event.preventDefault();
                          handleSelect(i);
                        }

                        if (event.key === " " && !isTypingRef.current) {
                          event.preventDefault();
                          handleSelect(i);
                        }
                      },
                    })}
                  >
                    {option.title}
                  </div>
                  {dividerIndex === i && (
                    <span className="tw-relative -tw-left-2 tw-my-1 tw-block tw-h-[1px] tw-w-[calc(100%_+_16px)] tw-bg-blue-100"></span>
                  )}
                </Fragment>
              ))}
            </div>
          </FloatingFocusManager>
        </FloatingPortal>
      )}

      {isInvalid && (
        <div className={`${CONTROLS_SELECTOR}__error`}>
          <Icon icon="ri-error-warning-fill" size={16} color="#E5281D" />
          {errorMessage || "Missing selected option"}
        </div>
      )}
    </div>
  );
};
